/**
 * MemorizModule -- Quran memorization integration.
 *
 * Phase 1: Renders a landing page with quick stats and action buttons.
 * Links to /Projects/Memoriz/ for the full experience.
 * Phase 2: Will import Memoriz components directly.
 */

import type { AweniContext, AweniModule } from '../../types';

export interface SurahProgress {
  surah: number;
  name: string;
  totalAyahs: number;
  memorized: number;
  percentage: number;
}

export interface DueCard {
  id: number;
  surah: number;
  ayah: number;
  text: string;
  stability: number;
  difficulty: number;
  dueAt: string;
}

interface MemorizStats {
  cardsDue: number;
  streak: number;
  surahsStarted: number;
}

function getMockStats(): MemorizStats {
  return { cardsDue: 12, streak: 7, surahsStarted: 5 };
}

export default class MemorizModule implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.context = ctx;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';

    let stats: MemorizStats;
    try {
      stats = await ctx.api.get<MemorizStats>('/aweni/api/v1/memoriz/stats');
    } catch {
      stats = getMockStats();
    }

    container.innerHTML = `
      <div class="memoriz-module px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">

        <!-- Hero -->
        <section class="rounded-2xl p-6 mb-6 text-center" style="background: linear-gradient(135deg, #F59E0B, #D97706);">
          <div class="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style="background: rgba(255,255,255,0.2)">
            <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/>
            </svg>
          </div>
          <h1 class="text-xl font-bold text-white mb-1">${t('memoriz_title')}</h1>
          <p class="text-sm text-white/80">${t('memoriz_subtitle')}</p>
        </section>

        <!-- Stats -->
        <section class="mb-6" role="region" aria-label="${t('memoriz_title')}">
          <div class="grid grid-cols-3 gap-3">
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #F59E0B">${stats.cardsDue}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('memoriz_cards_due')}</p>
            </div>
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #0A0A0A">${stats.streak}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('memoriz_streak')}</p>
              <p class="text-xs" style="color: var(--text-secondary)">${t('memoriz_streak_days', { count: stats.streak })}</p>
            </div>
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #8B5CF6">${stats.surahsStarted}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('memoriz_surahs_started')}</p>
            </div>
          </div>
        </section>

        <!-- Actions -->
        <section class="mb-6">
          <div class="grid grid-cols-2 gap-3">
            <button class="memoriz-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="review" aria-label="${t('memoriz_start_review')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #F59E0B20; color: #F59E0B">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 11h6M8 15h4"/></svg>
              </div>
              <div class="text-start">
                <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('memoriz_start_review')}</p>
                <p class="text-xs" style="color: var(--text-secondary)">${stats.cardsDue} ${t('memoriz_cards_due').toLowerCase()}</p>
              </div>
            </button>

            <button class="memoriz-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="surahs" aria-label="${t('memoriz_browse_surahs')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #8B5CF620; color: #8B5CF6">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
              </div>
              <div class="text-start">
                <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('memoriz_browse_surahs')}</p>
                <p class="text-xs" style="color: var(--text-secondary)">${stats.surahsStarted} ${t('memoriz_surahs_started').toLowerCase()}</p>
              </div>
            </button>

            <button class="memoriz-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="prayer" aria-label="${t('memoriz_prayer_times')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #0A0A0A20; color: #0A0A0A">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              </div>
              <div class="text-start">
                <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('memoriz_prayer_times')}</p>
              </div>
            </button>

            <button class="memoriz-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="dhikr" aria-label="${t('memoriz_dhikr_counter')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #3B82F620; color: #3B82F6">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l2 2"/></svg>
              </div>
              <div class="text-start">
                <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('memoriz_dhikr_counter')}</p>
              </div>
            </button>
          </div>
        </section>

        <!-- Open Full -->
        <section class="text-center">
          <a href="/Projects/Memoriz/" class="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-transform active:scale-95" style="background: #F59E0B; color: white;" target="_blank" rel="noopener" aria-label="${t('memoriz_open_full')}">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            ${t('memoriz_open_full')}
          </a>
        </section>
      </div>
    `;

    container.querySelectorAll<HTMLButtonElement>('.memoriz-action').forEach((btn) => {
      btn.addEventListener('click', () => this.handleAction(btn.dataset.action || ''));
    });
  }

  unmount(): void {
    if (this.container) {
      this.container.innerHTML = '';
      this.container = null;
    }
    this.context = null;
  }

  private handleAction(action: string): void {
    const routeMap: Record<string, string> = {
      review: '/Projects/Memoriz/#/review',
      surahs: '/Projects/Memoriz/#/surahs',
      prayer: '/Projects/Memoriz/#/prayer',
      dhikr: '/Projects/Memoriz/#/dhikr',
    };
    window.open(routeMap[action] || '/Projects/Memoriz/', '_blank', 'noopener');
  }
}
